/**
 * Discount Drug Program — field-level effects.
 *
 * Migrated from: patient/discount-drug-program-base.coffee
 *
 * Old validators → new effects:
 *   PopulatePhysVistDate → PopulatePhysVistDate
 *   SetStatus            → SetStatus
 *
 * New (not from legacy CoffeeScript):
 *   DiscountDrugProviderCheck → drives show_med_health_sections so the
 *   "Prescribed Medications" / "Health Services" sections hide when
 *   is_provider_available is "Hudson" or "ODA", and show for "Other"/empty.
 *   Registered as transform (not validate) so it runs on edit/read init,
 *   including when Drug Provider is empty (UI shows N/A).
 */
import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

// ─── PopulatePhysVistDate ────────────────────────────────────────────────────
// Old: DSLFx.Validators.PopulatePhysVistDate
// Trigger field: is_phys_visit
// When is_phys_visit = "Yes", fetches the latest patient_schedule and sets phys_visit_date.
export const PopulatePhysVistDate: FieldHandler = (
	field,
	{ form, formMode }
) => {
	void (async () => {
		if (formMode === "edit" && field.value !== "Yes") return;
		if (field.value !== "Yes") return;

		const patientId = form.getFieldState("patient_id")?.value?.id;
		if (!patientId) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(
					`/patient_schedule/list?limit=1&sort=-created_on&_patient_id=eq:${patientId}`
				)
			);
			const rows = Array.isArray(data) ? data : (data?.data ?? []);
			if (rows.length > 0 && rows[0]?.date) {
				form.setFieldState("phys_visit_date", (s) => {
					s.value = rows[0].date;
				});
			}
		} catch (err) {
			console.error("PopulatePhysVistDate: API error", err);
		}
	})();
	return "";
};

// ─── SetStatus ───────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.SetStatus
// Trigger field: phys_approved
// Sets status to "Approved" or "Denied" based on phys_approved value.
export const SetStatus: FieldHandler = (field, { form }) => {
	const phys = field.value;
	if (phys === "Yes") {
		form.setFieldState("status", (s) => {
			s.value = "Approved";
		});
	} else if (phys === "No") {
		form.setFieldState("status", (s) => {
			s.value = "Denied";
		});
	}
	return "";
};

// ─── DiscountDrugProviderCheck ─────────────────────────────────────────────
// Trigger field: is_provider_available (ui.transform)
// Hides "Prescribed Medications" / "Health Services" sections when the
// selected provider is "Hudson" or "ODA"; shows them for "Other" or empty.
// Physician visit / approval questions are not gated by this flag.
export const DiscountDrugProviderCheck: FieldHandler = (field, { form }) => {
	const provider = field.value;
	const hideSections = provider === "Hudson" || provider === "ODA";

	form.setFieldState("show_med_health_sections", (s) => {
		s.value = hideSections ? "No" : "Yes";
	});
	return "";
};
